import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, ArrowRight, ExternalLink, Mail } from "lucide-react";
import { MaskedLines, Reveal } from "@/components/motion";
import JsonLd from "@/components/JsonLd";
import { ARTICLES } from "@/constants/articles";
import { STATES } from "@/constants/states";
import { EMAIL } from "@/constants/site";
import { MODALITIES } from "@/constants/modalities";
import { usePageMeta } from "@/hooks/usePageMeta";

const Block = ({ block }) => {
  switch (block.type) {
    case "h2":
      return <h2 className="mt-12 font-heading text-lg font-extrabold tracking-tight text-ink">{block.text}</h2>;
    case "h3":
      return <h3 className="mt-8 font-heading text-base font-bold tracking-tight text-ink">{block.text}</h3>;
    case "list":
      return (
        <ul className="mt-4 flex flex-col gap-2.5">
          {block.items.map((item, i) => (
            <li key={i} className="flex items-start gap-3 text-base leading-relaxed text-ink/75">
              <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rotate-45 bg-blueprint" aria-hidden="true" />
              {item}
            </li>
          ))}
        </ul>
      );
    case "links":
      return (
        <div className="mt-4 flex flex-col gap-3">
          {block.items.map((l, i) => (
            <a
              key={i}
              href={l.href}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`article-link-${i}`}
              className="group flex w-fit items-center gap-2 border-b border-blueprint/30 pb-0.5 text-base font-medium text-blueprint transition-colors hover:border-blueprint"
            >
              {l.label}
              <ExternalLink size={14} className="transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </a>
          ))}
        </div>
      );
    default:
      return <p className="mt-5 text-base leading-relaxed text-ink/75">{block.text}</p>;
  }
};

const GuideCallout = ({ place, testid }) => (
  <div className="mt-8 border border-blueprint/30 bg-blueprint/5 p-6 md:p-7" data-testid={testid}>
    <p className="eyebrow">Get shielding help in {place}</p>
    <p className="mt-3 text-sm leading-relaxed text-ink/70">
      Planning an X-ray project in {place}? Most rooms need a shielding design before registration. Send the floor plan — quotes come back within one business day.
    </p>
    <Link
      to="/request"
      data-testid={`${testid}-cta`}
      className="group mt-5 inline-flex items-center gap-2 bg-blueprint px-5 py-3 text-sm font-semibold text-white transition-colors duration-200 hover:bg-ink"
    >
      Request a Shielding Design
      <ArrowUpRight size={15} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
    </Link>
  </div>
);

const Article = () => {
  const { slug } = useParams();
  const a = ARTICLES.find((x) => x.slug === slug);
  usePageMeta(
    a ? `${a.title} | Heartland Physics` : "Insights | Heartland Physics",
    a ? a.excerpt : "Practical guidance on radiation shielding design from Heartland Physics."
  );

  const isGuide = a?.tag === "Registration Guide";
  const place = isGuide
    ? (a.title.match(/in (.+?)(?::|$)/)?.[1] || "your state")
    : null;
  const statePage = isGuide
    ? STATES.find((s) => s.guideSlug === a.slug || s.extraGuides.some((g) => g.slug === a.slug))
    : null;
  const modalityPage = a?.modality ? MODALITIES.find((m) => m.slug === a.modality) : null;

  if (!a) {
    return (
      <section className="pt-16">
        <div className="mx-auto max-w-7xl px-5 py-24 md:px-8">
          <p className="eyebrow">Not found</p>
          <h1 className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-ink">That article doesn't exist.</h1>
          <Link to="/insights" data-testid="article-404-back" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-blueprint">
            <ArrowLeft size={15} /> Back to insights
          </Link>
        </div>
      </section>
    );
  }

  const titleLines = a.title.length > 42
    ? [a.title.slice(0, a.title.lastIndexOf(" ", 42)), a.title.slice(a.title.lastIndexOf(" ", 42) + 1)]
    : [a.title];

  return (
    <>
      <JsonLd
        id={`article-${a.slug.slice(0, 40)}`}
        data={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: a.title,
          description: a.excerpt,
          author: { "@type": "Person", name: "Lancer Smith", jobTitle: "Medical Health Physicist" },
          publisher: { "@type": "Organization", name: "Heartland Physics" },
          mainEntityOfPage: `https://heartlandphysics.com/insights/${a.slug}`,
        }}
      />
      <section data-testid="article-hero" className="border-b border-border bg-blueprint-grid pt-16">
        <div className="mx-auto max-w-3xl px-5 pb-12 pt-14 md:px-8 md:pb-16 md:pt-20">
          <Reveal y={12}>
            <Link to="/insights" data-testid="article-back-link" className="group inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.22em] text-ink/50 transition-colors hover:text-blueprint">
              <ArrowLeft size={14} className="transition-transform duration-200 group-hover:-translate-x-1" /> Insights
            </Link>
          </Reveal>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-blueprint">{a.tag}</span>
          </div>
          <h1 className="mt-5 font-heading text-3xl font-extrabold leading-[1.12] tracking-tight text-ink sm:text-4xl lg:text-5xl">
            <MaskedLines delay={0.1} lines={titleLines} />
          </h1>
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto max-w-3xl px-5 py-12 md:px-8 md:py-16" data-testid="article-body">
          {a.blocks.map((b, i) => (
            <Reveal key={i} y={14}>
              <Block block={b} />
              {isGuide && i === 0 && <GuideCallout place={place} testid="guide-crosslink" />}
            </Reveal>
          ))}
          {isGuide && (
            <Reveal y={14}>
              <GuideCallout place={place} testid="guide-crosslink-bottom" />
            </Reveal>
          )}
          {statePage && (
            <Reveal y={14}>
              <Link
                to={`/shielding-requirements/${statePage.slug}`}
                data-testid="article-state-req-link"
                className="group mt-6 flex items-center justify-between border border-border bg-white p-5 transition-colors duration-200 hover:border-blueprint"
              >
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink/50">State requirements</p>
                  <p className="mt-1.5 font-heading text-sm font-bold tracking-tight text-ink">
                    X-ray shielding requirements in {statePage.name}
                  </p>
                </div>
                <ArrowRight size={16} className="shrink-0 text-blueprint transition-transform duration-200 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          )}
          {modalityPage && (
            <Reveal y={14}>
              <Link
                to={`/shielding-design/${modalityPage.slug}`}
                data-testid="article-modality-link"
                className="group mt-6 flex items-center justify-between border border-border bg-white p-5 transition-colors duration-200 hover:border-blueprint"
              >
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink/50">Our service</p>
                  <p className="mt-1.5 font-heading text-sm font-bold tracking-tight text-ink">
                    {modalityPage.name} shielding design
                  </p>
                </div>
                <ArrowRight size={16} className="shrink-0 text-blueprint transition-transform duration-200 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          )}
          {a.updated && (
            <p className="mt-12 border-t border-border pt-6 font-mono text-xs uppercase tracking-[0.18em] text-ink/45" data-testid="article-updated">
              {a.updated}
            </p>
          )}
        </div>
      </section>

      <section className="border-t border-border bg-ink text-white">
        <div className="bg-blueprint-grid-dark">
          <div className="mx-auto max-w-3xl px-5 py-14 md:px-8 md:py-16">
            <Reveal>
              <p className="eyebrow !text-azure">Next step</p>
              <h2 className="mt-4 font-heading text-2xl font-extrabold tracking-tight">Need a shielding design for your project?</h2>
              <p className="mt-4 max-w-xl text-sm leading-relaxed text-white/60">
                Send the basics through the structured request form — we respond within one business day.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link
                  to="/request"
                  data-testid="article-cta-request"
                  className="group flex items-center gap-2 bg-white px-6 py-3.5 text-sm font-semibold text-ink transition-colors duration-200 hover:bg-azure hover:text-white"
                >
                  Request a Shielding Design
                  <ArrowUpRight size={15} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
                <a
                  href={`mailto:${EMAIL}`}
                  data-testid="article-cta-email"
                  className="flex items-center gap-2 border border-white/30 px-6 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:border-azure hover:text-azure"
                >
                  <Mail size={15} /> Email Lancer@HeartlandPhysics.com
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
};

export default Article;
